class AnnouncementBar extends HTMLElement {
    constructor() {
        super();

        this.elements = {
            announcementBar: this.querySelector('[data-announcement-bar]'),
            countdown: this.querySelector('[data-countdown]'),
            countdownMobile: this.querySelector('[data-countdown-mobile]'),
            slider: this.querySelector('[data-announcement-slider]')
        };

        if (!this.elements.announcementBar) {
            return;
        }

        // Initialize slider if enabled
        if (this.elements.slider && this.dataset.useSlider === 'true') {
            this.initSlider();
        }

        // Show the announcement bar with a slight delay for smooth transition
        setTimeout(() => {
            this.elements.announcementBar.classList.remove('is-invisible');
            this.elements.announcementBar.removeAttribute('aria-hidden');
        }, 100);

        // Initialize countdown if present
        if (this.elements.countdown || this.elements.countdownMobile) {
            this.setupCountdown();
        }
    }

    initSlider() {
        // Wait for Splide to be available
        const initSplide = () => {
            if (typeof Splide === 'undefined') {
                setTimeout(initSplide, 50);
                return;
            }

            this.splide = new Splide(this.elements.slider, {
                // Cross-fade between messages instead of sliding
                type: 'fade',
                rewind: true,
                // Autoplay is handled manually below so the pause/play control,
                // hover/focus pausing and reduced-motion handling stay in sync (ADA).
                autoplay: false,
                // Use our own markup/handlers for arrows (keeps ARIA labels intact).
                arrows: false,
                pagination: false,
                speed: 400,
                label: 'Announcements',
                padding: { left: '32px', right: '32px' },
                gap: '32px',
                easing: 'ease-in-out',
                breakpoints: {
                    768: {
                        padding: { left: '16px', right: '16px' },
                        gap: '16px'
                    }
                }
            });

            this.splide.mount();

            // Wire up our custom prev/next arrows
            this.setupArrows();

            // Accessible auto-rotation
            this.setupAutoplay();

            // Initialize countdown for each slide
            this.initSlideCountdowns();
        };

        initSplide();
    }

    setupArrows() {
        const prev = this.elements.slider.querySelector('.section-announcement-bar__slider-arrow--prev');
        const next = this.elements.slider.querySelector('.section-announcement-bar__slider-arrow--next');

        if (prev) {
            prev.addEventListener('click', () => this.splide && this.splide.go('<'));
        }
        if (next) {
            next.addEventListener('click', () => this.splide && this.splide.go('>'));
        }
    }

    setupAutoplay() {
        this.autoplayInterval = parseInt(this.dataset.autoplayInterval, 10) || 5000;

        const wantsAutoplay = this.dataset.autoplay === 'true';
        const prefersReducedMotion =
            window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // The visitor-facing play/pause intent. Hover/focus pause the timer
        // without changing intent, so leaving the bar resumes only if still "playing".
        this.autoplayIntent = wantsAutoplay && !prefersReducedMotion;

        // Pause while hovered or keyboard-focused, resume after (WCAG 2.2.2)
        const slider = this.elements.slider;
        ['mouseenter', 'focusin'].forEach((evt) =>
            slider.addEventListener(evt, () => this.stopAutoplayTimer())
        );
        ['mouseleave', 'focusout'].forEach((evt) =>
            slider.addEventListener(evt, () => this.resumeAutoplay())
        );

        if (this.autoplayIntent) {
            this.resumeAutoplay();
        }
    }

    resumeAutoplay() {
        // Only resume when the visitor hasn't explicitly paused rotation
        if (!this.autoplayIntent || !this.splide) return;
        this.stopAutoplayTimer();
        this.autoplayTimer = setInterval(() => {
            if (this.splide) {
                this.splide.go('>');
            }
        }, this.autoplayInterval);
    }

    stopAutoplayTimer() {
        if (this.autoplayTimer) {
            clearInterval(this.autoplayTimer);
            this.autoplayTimer = null;
        }
    }

    initSlideCountdowns() {
        const slideCountdowns = this.querySelectorAll('[data-countdown-slide]');
        if (!slideCountdowns.length) return;

        this.slideCountdownElements = [];

        slideCountdowns.forEach(container => {
            const endTime = container.dataset.countdownEndTime;
            if (!endTime) return;

            const elements = {
                container,
                endTime,
                days: null,
                hours: null,
                minutes: null,
                seconds: null
            };

            // Create countdown elements
            container.innerHTML = '';
            elements.days = document.createElement('span');
            elements.hours = document.createElement('span');
            elements.minutes = document.createElement('span');
            elements.seconds = document.createElement('span');

            [elements.days, elements.hours, elements.minutes, elements.seconds].forEach(el => {
                el.textContent = '00';
            });

            container.appendChild(elements.days);
            container.appendChild(document.createTextNode(':'));
            container.appendChild(elements.hours);
            container.appendChild(document.createTextNode(':'));
            container.appendChild(elements.minutes);
            container.appendChild(document.createTextNode(':'));
            container.appendChild(elements.seconds);

            this.slideCountdownElements.push(elements);
        });

        // Start interval for slide countdowns
        this.slideCountdownInterval = setInterval(() => {
            this.updateSlideCountdowns();
        }, 1000);

        this.updateSlideCountdowns();
    }

    updateSlideCountdowns() {
        if (!this.slideCountdownElements) return;

        const now = new Date();

        this.slideCountdownElements.forEach(elements => {
            const endTime = new Date(parseInt(elements.endTime) * 1000);
            const timeLeft = Math.floor((endTime.getTime() - now.getTime()) / 1000);

            if (timeLeft <= 0) {
                elements.days.textContent = '00';
                elements.hours.textContent = '00';
                elements.minutes.textContent = '00';
                elements.seconds.textContent = '00';
                elements.container.classList.add('countdown-expired');
                return;
            }

            const days = Math.floor(timeLeft / (24 * 60 * 60));
            const hours = Math.floor((timeLeft % (24 * 60 * 60)) / (60 * 60));
            const minutes = Math.floor((timeLeft % (60 * 60)) / 60);
            const seconds = timeLeft % 60;

            elements.days.textContent = this.padZero(days);
            elements.hours.textContent = this.padZero(hours);
            elements.minutes.textContent = this.padZero(minutes);
            elements.seconds.textContent = this.padZero(seconds);
        });
    }

    setupCountdown() {
        // Get end time from either countdown element
        const countdownEl = this.elements.countdown || this.elements.countdownMobile;
        this.countdownEndTime = countdownEl.dataset.countdownEndTime;

        if (!this.countdownEndTime) {
            return;
        }

        // Create countdown elements for desktop
        if (this.elements.countdown) {
            this.createCountdownElements(this.elements.countdown);
        }

        // Create countdown elements for mobile
        if (this.elements.countdownMobile) {
            this.createCountdownElements(this.elements.countdownMobile, true);
        }

        // Start the countdown
        this.countdownInterval = setInterval(() => {
            this.updateCountdown();
        }, 1000);

        // Initial update
        this.updateCountdown();
    }

    createCountdownElements(container, isMobile = false) {
        container.innerHTML = '';

        const elements = {
            days: document.createElement('span'),
            hours: document.createElement('span'),
            minutes: document.createElement('span'),
            seconds: document.createElement('span')
        };

        Object.values(elements).forEach(element => {
            element.textContent = '00';
        });

        container.appendChild(elements.days);
        container.appendChild(document.createTextNode(':'));
        container.appendChild(elements.hours);
        container.appendChild(document.createTextNode(':'));
        container.appendChild(elements.minutes);
        container.appendChild(document.createTextNode(':'));
        container.appendChild(elements.seconds);

        if (isMobile) {
            this.countdownElementsMobile = elements;
        } else {
            this.countdownElements = elements;
        }
    }

    updateCountdown() {
        const now = new Date();
        const endTime = new Date(parseInt(this.countdownEndTime) * 1000);
        const timeLeft = Math.floor((endTime.getTime() - now.getTime()) / 1000);

        if (timeLeft <= 0) {
            this.onCountdownEnd();
            return;
        }

        const days = Math.floor(timeLeft / (24 * 60 * 60));
        const hours = Math.floor((timeLeft % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((timeLeft % (60 * 60)) / 60);
        const seconds = timeLeft % 60;

        // Update desktop countdown
        if (this.countdownElements) {
            this.countdownElements.days.textContent = this.padZero(days);
            this.countdownElements.hours.textContent = this.padZero(hours);
            this.countdownElements.minutes.textContent = this.padZero(minutes);
            this.countdownElements.seconds.textContent = this.padZero(seconds);
        }

        // Update mobile countdown
        if (this.countdownElementsMobile) {
            this.countdownElementsMobile.days.textContent = this.padZero(days);
            this.countdownElementsMobile.hours.textContent = this.padZero(hours);
            this.countdownElementsMobile.minutes.textContent = this.padZero(minutes);
            this.countdownElementsMobile.seconds.textContent = this.padZero(seconds);
        }
    }

    padZero(num) {
        return num.toString().padStart(2, '0');
    }

    onCountdownEnd() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }

        // Reset desktop countdown
        if (this.countdownElements) {
            Object.values(this.countdownElements).forEach(element => {
                element.textContent = '00';
            });
            if (this.elements.countdown) {
                this.elements.countdown.classList.add('countdown-expired');
            }
        }

        // Reset mobile countdown
        if (this.countdownElementsMobile) {
            Object.values(this.countdownElementsMobile).forEach(element => {
                element.textContent = '00';
            });
            if (this.elements.countdownMobile) {
                this.elements.countdownMobile.classList.add('countdown-expired');
            }
        }
    }

    disconnectedCallback() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        if (this.slideCountdownInterval) {
            clearInterval(this.slideCountdownInterval);
        }
        this.stopAutoplayTimer();
        if (this.splide) {
            this.splide.destroy();
        }
    }
}

if (customElements.get("announcement-bar") === undefined) {
    customElements.define("announcement-bar", AnnouncementBar);
}